/**
 * AuthorityRefs — small server component that renders a "Going deeper"
 * card row at the bottom of a service or audience page, linking out to
 * the relevant deep authority reference pages.
 *
 * Why this exists: internal cross-linking is the cheapest SEO signal we
 * have. Every service page that links to its corresponding deep
 * authority page (water-damage → water-damage-categories,
 * mold-remediation → mold-conditions, etc.) compounds topic-cluster
 * authority on the destination AND helps AI search engines reconcile
 * the two pages as part of one body of expertise.
 */

import Link from "next/link";

export type AuthorityRef = {
  href: string;
  label: string;
  /** Short eyebrow tag, e.g. "IICRC S500" or "Insurance Claims" */
  tag: string;
};

export function AuthorityRefs({
  heading = "Going deeper",
  refs,
}: {
  heading?: string;
  refs: AuthorityRef[];
}) {
  if (refs.length === 0) return null;
  return (
    <section
      style={{
        background: "rgba(8,15,26,0.7)",
        padding: "60px 32px",
        borderTop: "1px solid rgba(212,168,75,0.18)",
      }}
    >
      <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
        <p
          style={{
            color: "#D4A84B",
            fontFamily: '"DM Mono", monospace',
            fontSize: "11px",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          Technical References
        </p>
        <h2
          style={{
            color: "#ffffff",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)",
            marginBottom: "24px",
          }}
        >
          {heading}
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "12px",
          }}
        >
          {refs.map((r) => (
            <Link
              key={r.href}
              href={r.href}
              style={{
                background: "rgba(5,13,24,0.5)",
                border: "1px solid rgba(212,168,75,0.25)",
                borderRadius: "12px",
                padding: "1rem 1.25rem",
                textDecoration: "none",
                textAlign: "left",
                display: "block",
              }}
            >
              <span
                style={{
                  color: "#D4A84B",
                  fontFamily: '"DM Mono", monospace',
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                {r.tag}
              </span>
              <span
                style={{
                  color: "#fff",
                  fontFamily: '"Barlow", sans-serif',
                  fontWeight: 700,
                  fontSize: "0.95rem",
                }}
              >
                {r.label} →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
